import { filterClasses } from "@swivl-mono/helpers";
import { BotSequenceJitFreshnessUnit, BotSequenceJitSettingValue, BotSequenceType, FMSCapabilityState, FMSType } from "@swivl-mono/types";
import { Tooltip } from "@swivl-mono/ui";

// Display order and labels for providers — shared by the capability matrix and the FMS
// integration page's JIT freshness section so both render the same catalog.
export const PROVIDER_ORDER: FMSType[] = [
  FMSType.StorEdge,
  FMSType.SSMCloud,
  FMSType.SiteLink,
  FMSType.Monument,
  FMSType.Cubby,
  FMSType.QuikStor,
  FMSType.Tenant,
  FMSType.Yardi
];

export const PROVIDER_LABELS: Partial<Record<FMSType, string>> = {
  [FMSType.SSMCloud]: "SSM Cloud"
};

export const JIT_SEQUENCE_ORDER: BotSequenceType[] = [
  BotSequenceType.PAST_DUE,
  BotSequenceType.ABANDONED_CART,
  BotSequenceType.MOVE_IN,
  BotSequenceType.MOVE_OUT
];

export const UNIT_LABELS: Record<BotSequenceJitFreshnessUnit, string> = {
  [BotSequenceJitFreshnessUnit.MINUTES]: "minutes",
  [BotSequenceJitFreshnessUnit.HOURS]: "hours",
  [BotSequenceJitFreshnessUnit.DAYS]: "days"
};

const UNIT_ABBR: Record<BotSequenceJitFreshnessUnit, string> = {
  [BotSequenceJitFreshnessUnit.MINUTES]: "min",
  [BotSequenceJitFreshnessUnit.HOURS]: "hr",
  [BotSequenceJitFreshnessUnit.DAYS]: "d"
};

/** The window exactly as entered — never re-derived into another unit. */
export function formatWindow(amount: number, unit: BotSequenceJitFreshnessUnit): string {
  return `${amount} ${UNIT_ABBR[unit]}`;
}

interface FreshnessChipProps {
  capability: FMSCapabilityState | undefined;
  /** The cell stored on the FMS integration for this sequence type; undefined = nothing stored. */
  setting: BotSequenceJitSettingValue | undefined;
  defaultMinutes: number;
}

/**
 * One sequence type's JIT freshness value: default / custom / disabled / no JIT. ORANGE =
 * a stored customization on the integration; light grey = the global default (`default`
 * tag, nothing stored).
 */
export function FreshnessChip({ capability, setting, defaultMinutes }: FreshnessChipProps) {
  const base = "inline-flex items-baseline gap-1.5 whitespace-nowrap rounded px-2 py-1 font-mono text-xs font-bold";
  const chipClasses = "border border-orange-200 bg-orange-50 text-orange-800";
  const tagClasses = "font-sans text-[9px] font-bold tracking-wide text-orange-700 uppercase";
  const tagLabel = "custom";

  if (capability !== FMSCapabilityState.Implemented) {
    return (
      <Tooltip content={`JIT capability is "${capability ?? FMSCapabilityState.TBD}" — the verifier falls through, settings have no effect.`}>
        <span className={filterClasses(base, "border border-dashed border-grey-300 bg-white font-normal text-grey-500")}>
          no JIT ({capability ?? FMSCapabilityState.TBD})
        </span>
      </Tooltip>
    );
  }
  if (setting && !setting.enabled) {
    return (
      <Tooltip content="JIT is disabled for this sequence type on this integration — fires proceed on the entity cache.">
        <span className={filterClasses(base, chipClasses)}>
          JIT Disabled
          <span className={tagClasses}>{tagLabel}</span>
        </span>
      </Tooltip>
    );
  }
  if (setting && setting.freshnessAmount != null && setting.freshnessUnit != null) {
    return (
      <span className={filterClasses(base, chipClasses)}>
        {formatWindow(setting.freshnessAmount, setting.freshnessUnit)}
        <span className={tagClasses}>{tagLabel}</span>
      </span>
    );
  }
  if (setting) {
    // An explicit enabled cell with the DEFAULT window (e.g. hand-written) — a stored
    // value, not the same as "nothing set". The API never writes one (all-defaults cells
    // are removed), so this is rare.
    return (
      <Tooltip content="JIT is explicitly enabled on this integration; the freshness window is the global default.">
        <span className={filterClasses(base, chipClasses)}>
          {defaultMinutes} min
          <span className={tagClasses}>{tagLabel}</span>
        </span>
      </Tooltip>
    );
  }
  return (
    <span className={filterClasses(base, "bg-grey-100 text-grey-700")}>
      {defaultMinutes} min
      <span className="font-sans text-[9px] font-bold tracking-wide text-grey-500 uppercase">default</span>
    </span>
  );
}
